import { useState, useEffect, useRef } from "react";
import { X, MapPin, Coffee, Map, Compass, ChevronRight } from "lucide-react";
import coorg from '../assets/coorg.jpeg';
import bir from '../assets/bir.jpeg';
import leh from '../assets/leh.jpeg';
import udaipur from '../assets/udaipur.jpeg';
import jaiselmer from '../assets/jaiselmer.jpeg';

// ─────────────────────────────────────────────────────────────────────────────
// DATA — 8 cities, fully populated
// ─────────────────────────────────────────────────────────────────────────────
const CITIES = [
  {
    id: "coorg",
    cityName: "Coorg",
    tagline: "Scotland of India",
    image: coorg,
    description:
      "Tucked among rolling hills draped in coffee and cardamom plantations, Coorg is a misty highland retreat where waterfalls cascade through dense forest and every breath smells of rain-soaked earth.",
    whatToDo: [
      "Trek to Tadiandamol — Coorg's highest peak",
      "Visit Abbey & Iruppu Falls after the monsoon",
      "Explore Namdroling Monastery's Golden Temple",
      "Go white-water rafting on the River Barapole",
      "Walk through a working coffee estate at sunrise",
      "Spot elephants at Nagarhole National Park",
    ],
    cafes: [
      "Coorg Wilderness Resort Café — coffee with a canopy view",
      "Capital Village Restaurant — authentic Kodava pork curry",
      "Raintree Café, Madikeri — great filter coffee & akki roti",
      "Honey Valley Estate Dining — farm-to-table hillside meals",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Arrive in Madikeri, check in, and stroll Raja's Seat at sunset. Dinner of Kodava thali at a local homestay.",
      },
      {
        day: "Day 2",
        plan: "Morning trek to Tadiandamol peak. Afternoon visit to Abbey Falls and a guided coffee estate tour.",
      },
      {
        day: "Day 3",
        plan: "Namdroling Monastery in the morning, rafting on Barapole river in the afternoon, and a bonfire farewell evening.",
      },
    ],
  },
  {
    id: "leh",
    cityName: "Leh",
    tagline: "Roof of the World",
    image: leh,
    description:
      "Perched at 3,500 m in the Himalayas, Leh is a high-altitude dreamscape of barren moonscapes, turquoise lakes, ancient monasteries, and skies so clear the Milky Way feels within reach.",
    whatToDo: [
      "Ride the legendary Khardung La — world's highest motorable road",
      "Kayak on the surreal Pangong Tso Lake",
      "Explore Thiksey and Hemis monasteries",
      "Experience the Nubra Valley and Bactrian camel safari",
      "White-water raft through the Zanskar Gorge",
      "Star-gaze at Tso Moriri — zero light pollution",
    ],
    cafes: [
      "Lamayuru Restaurant — momos with a monastery backdrop",
      "The Tibetan Kitchen — hearty thukpa and butter tea",
      "Bon Appétit Café — espresso at 3,500 m",
      "Gesmo Restaurant — legendary apple pie and pasta",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Arrive and acclimatise. Light walk to Leh Palace and Shanti Stupa. Rest and hot soup.",
      },
      {
        day: "Day 2",
        plan: "Drive to Pangong Lake via Chang La. Sunset picnic on the blue shore. Overnight under the stars.",
      },
      {
        day: "Day 3",
        plan: "Nubra Valley via Khardung La. Camel safari at Hunder sand dunes. Apricot orchards and local dinner.",
      },
    ],
  },
  {
    id: "jaisalmer",
    cityName: "Jaisalmer",
    tagline: "The Golden City",
    image: jaiselmer,
    description:
      "Rising from the Thar Desert like a mirage, Jaisalmer's honey-coloured sandstone fort and labyrinthine havelis glow at golden hour. Camel silhouettes on dunes and folk music under a billion stars complete the spell.",
    whatToDo: [
      "Overnight camel safari at Sam Sand Dunes",
      "Explore the living Jaisalmer Fort at dawn",
      "Visit Patwon Ki Haveli's intricate jharokhas",
      "Sunset at Gadisar Lake with folk musicians",
      "Quad biking across the Thar Desert",
      "Shop raw silk and mirror-work textiles in the old bazaar",
    ],
    cafes: [
      "Saffron Restaurant — rooftop dal baati churma with fort views",
      "Chokhi Dhani Desert Camp Dining — royal Rajasthani thali",
      "Trio Restaurant — great lassies and ker sangri sabji",
      "Jaisal Italy — surprising wood-fired pizza inside the fort",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Check into a heritage haveli. Walk the fort's narrow lanes, visit Jain temples, and catch sunset over the golden ramparts.",
      },
      {
        day: "Day 2",
        plan: "Full-day desert: quad bikes at Khuri, afternoon camel ride to Sam Dunes, bonfire dinner with live folk music under the stars.",
      },
      {
        day: "Day 3",
        plan: "Gadisar Lake rowboat at dawn, Patwon Ki Haveli tour, then browse the bazaar before departing.",
      },
    ],
  },
  {
    id: "gurez",
    cityName: "Gurez",
    tagline: "Kashmir's Hidden Valley",
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=900&q=80",
    description:
      "Gurez is Kashmir's best-kept secret — a glacial valley carved by the roaring Kishanganga river, ringed by 4,000 m peaks, and inhabited by the ancient Dard-Shin people whose wooden villages have barely changed in centuries.",
    whatToDo: [
      "Trek the meadows of Tulail Valley",
      "Fish for rainbow trout in the Kishanganga river",
      "Drive to Razdan Pass at 3,300 m for panoramic views",
      "Visit Habba Khatoon Peak (named after the legendary Kashmiri poetess)",
      "Interact with Dard Shin communities and hear local folk songs",
      "Wildflower walks in Jumagund meadows",
    ],
    cafes: [
      "Army-run Canteen — the only real food stop, surprisingly good",
      "Dawar Dhabas — local kahwa and roti with honey",
      "Homestay meals — freshly baked tchot bread with walnut chutney",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Drive from Bandipora (4–5 hrs). Arrive in Dawar, settle into a wooden homestay. Evening walk along the Kishanganga.",
      },
      {
        day: "Day 2",
        plan: "Hike to Habba Khatoon Peak. Picnic lunch in the alpine meadow. Trout fishing in the afternoon river.",
      },
      {
        day: "Day 3",
        plan: "Drive to Razdan Pass for sunrise. Explore Tulail Village, then descend back to Bandipora by evening.",
      },
    ],
  },
  {
    id: "jaipur",
    cityName: "Jaipur",
    tagline: "The Pink City",
    image: "https://images.unsplash.com/photo-1599661046289-e31897846e41?w=900&q=80",
    description:
      "Painted a warm terracotta-pink by royal decree, Jaipur is a living museum — majestic forts, palace courtyards, gem bazaars, and the irresistible aroma of kachori frying in ghee all collide in one glorious city.",
    whatToDo: [
      "Sunrise at Amber Fort before the crowds arrive",
      "Explore City Palace's lavish state rooms",
      "Photograph Hawa Mahal's honeycomb facade at golden hour",
      "Hot-air balloon ride over the Thar at dawn",
      "Shop blue pottery and block-print textiles at Johari Bazaar",
      "Step-well exploring at Panna Meena Ka Kund",
    ],
    cafes: [
      "Lassiwala — legendary Jaipur lassi, thick as a milkshake",
      "Tapri Tea Post — outdoor chai in a heritage courtyard",
      "Peacock Rooftop Restaurant — biryanis with Hawa Mahal views",
      "Bar Palladio — Italian-Rajput fusion in a turquoise garden pavilion",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Arrive, check into a heritage haveli. Explore City Palace and Jantar Mantar. Sunset drinks on a rooftop overlooking the Pink City.",
      },
      {
        day: "Day 2",
        plan: "Dawn balloon flight. Amber Fort elephant ride. Afternoon block-print workshop and Johari Bazaar gem shopping.",
      },
      {
        day: "Day 3",
        plan: "Hawa Mahal sunrise shoot. Step-well exploration. Lunch at a biryani house then depart.",
      },
    ],
  },
  {
    id: "lonavala",
    cityName: "Lonavala",
    tagline: "Mumbai's Misty Escape",
    image: "https://images.unsplash.com/photo-1689172324767-f180880ccdec?q=80&w=900",
    description:
      "Just two hours from Mumbai and Pune, Lonavala transforms into a cascading green paradise every monsoon. Ancient Buddhist caves, dramatic cliff-top forts, and the legendary chikki sweet make it the perfect weekend reset.",
    whatToDo: [
      "Trek to Rajmachi Fort through the monsoon forest",
      "Explore Karla and Bhaja Buddhist rock-cut caves",
      "Canyon swing at Della Adventure Park",
      "Sunrise at Tiger's Leap viewpoint over the valley",
      "Night trek up Visapur Fort",
      "Swim in natural waterfall pools at Bhushi Dam during monsoon",
    ],
    cafes: [
      "Maganlal Chikki — the original, unmissable walnut and peanut chikki shop",
      "The Dukes Retreat Café — English breakfast with valley views",
      "Roys Restaurant — old-school Parsi salli boti since 1975",
      "Café Gustoso — thin-crust pizza with misty hill views",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Drive up from Mumbai or Pune. Check in, visit Bhushi Dam, buy chikki from Maganlal. Evening bonfire at the villa.",
      },
      {
        day: "Day 2",
        plan: "Trek to Rajmachi Fort. Picnic lunch on the ramparts. Afternoon at Karla Caves, then sunset at Tiger's Leap.",
      },
      {
        day: "Day 3",
        plan: "Bhaja Caves morning walk, Della Adventure Park for a thrill, and a leisurely drive back down the Ghats.",
      },
    ],
  },
  {
    id: "bir",
    cityName: "Bir",
    tagline: "Paragliding Capital of India",
    image: bir,
    description:
      "Bir Billing is where Himalayan thermals and Tibetan spirituality meet. The world-class paragliding launch site, monasteries painted saffron and crimson, and a surprisingly cosmopolitan café culture make this tiny village utterly addictive.",
    whatToDo: [
      "Tandem paraglide from Billing (2,400 m) over the Kangra Valley",
      "Attend early morning puja at Chokling Monastery",
      "Mountain-bike the Bir-Baijnath forest trail",
      "Learn Tibetan thangka painting at a local studio",
      "Hike to Thamsar Pass through the Dhauladhar range",
      "Watch the sunset from the Deer Park Institute garden",
    ],
    cafes: [
      "Illiterati Books & Coffee — specialty coffee, secondhand books, and good Wi-Fi",
      "Café 1947 — Israeli breakfast bowls beloved by backpackers",
      "Om Shanti Café — rooftop views, momos, and turmeric lattes",
      "Andretta Potter's Colony Café — ceramics and chai 30 minutes away",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Arrive in Bir. Check into a camp or cottage. Explore the Tibetan Colony, browse thangka shops, and watch paragliders land.",
      },
      {
        day: "Day 2",
        plan: "Full paragliding experience from Billing at sunrise. Afternoon monastery visit and thangka painting class.",
      },
      {
        day: "Day 3",
        plan: "Mountain bike ride through the forest. Sunset at Deer Park Institute. Star-gazing from the hillside camp.",
      },
    ],
  },
  {
    id: "udaipur",
    cityName: "Udaipur",
    tagline: "City of Lakes",
    image: udaipur,
    description:
      "Shimmering lake palaces, flower-decked ghats, and whitewashed havelis reflected in still water — Udaipur is Rajasthan's most romantic city, a place where every corner unfolds like a painting and every evening feels like a fairytale.",
    whatToDo: [
      "Boat ride on Lake Pichola to Jag Mandir at dusk",
      "Explore the City Palace's panoramic terraces",
      "Watch the puppet show and folk dance at Bagore Ki Haveli",
      "Cycle through the old city's narrow marble lanes",
      "Sunrise yoga at the Monsoon Palace hillside",
      "Day trip to Ranakpur's extraordinary Jain temples",
    ],
    cafes: [
      "Upre by 1559 — rooftop dining floating above Lake Pichola",
      "Ambrai Restaurant — candlelit dinners with palace reflections",
      "Jagat Niwas Palace Rooftop — sunset cocktails and Rajasthani thali",
      "Millets of Mewar — healthy bowls in a heritage courtyard",
    ],
    itinerary: [
      {
        day: "Day 1",
        plan: "Arrive and check into a lake-view heritage property. Evening boat ride on Pichola, sunset at Sajjangarh Monsoon Palace.",
      },
      {
        day: "Day 2",
        plan: "City Palace in the morning. Afternoon cycle through the old city. Puppet show at Bagore Ki Haveli. Rooftop dinner with palace views.",
      },
      {
        day: "Day 3",
        plan: "Day trip to Ranakpur Jain temples. Return for a sunset drink on the lake, then depart.",
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// TAB CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "whatToDo", label: "What to Do", icon: Compass },
  { id: "cafes", label: "Cafés & Eats", icon: Coffee },
  { id: "itinerary", label: "Itinerary", icon: Map },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export default function ComingSoonSection() {
  const [selected, setSelected] = useState(null);

  // Lock scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selected]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 my-16" data-testid="coming-soon-section">
        {/* Header */}
        <div className="mb-10">
          <div className="ols-label mb-2">Properties Landing Soon</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <h2 className="font-display text-3xl md:text-4xl">
              Destinations We're Curating
            </h2>
            <p className="text-stone-500 text-sm max-w-sm">
              Our team is hand-picking exceptional stays in these locations. Click a city to explore what's waiting for you.
            </p>
          </div>
        </div>

        {/* City Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CITIES.map((city) => (
            <CityCard key={city.id} city={city} onClick={() => setSelected(city)} />
          ))}
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <CityModal city={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CITY CARD
// ─────────────────────────────────────────────────────────────────────────────
function CityCard({ city, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Explore ${city.cityName}`}
      className="group relative text-left rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      data-testid={`city-card-${city.id}`}
    >
      {/* Image */}
      <div className="aspect-[4/5] overflow-hidden bg-stone-200">
        <img
          src={city.image}
          alt={city.cityName}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
          style={{ "--tw-scale-x": "1.08", "--tw-scale-y": "1.08" }}
        />
      </div>

      {/* Permanent gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Coming Soon badge */}
      <div className="absolute top-3 right-3">
        <span className="badge badge-dark text-[0.55rem] tracking-widest opacity-90">
          Coming Soon
        </span>
      </div>

      {/* Text at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="flex items-center gap-1 text-white/60 text-xs mb-0.5">
              <MapPin className="w-3 h-3" />
              <span>{city.tagline}</span>
            </div>
            <div className="font-display text-xl text-white">{city.cityName}</div>
          </div>
          {/* Explore arrow */}
          <div className="w-8 h-8 rounded-full bg-white/15 border border-white/30 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all duration-300 shrink-0">
            <ChevronRight className="w-4 h-4 text-white group-hover:text-stone-900 transition-colors duration-300" />
          </div>
        </div>

        {/* Description — slides up on hover */}
        <p className="text-white/75 text-xs leading-relaxed mt-2 line-clamp-2 max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500 ease-out">
          {city.description}
        </p>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CITY MODAL
// ─────────────────────────────────────────────────────────────────────────────
function CityModal({ city, onClose }) {
  const [tab, setTab] = useState("whatToDo");
  const overlayRef = useRef(null);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ animation: "ols-fade-up 0.25s cubic-bezier(0.22,1,0.36,1) both" }}
      data-testid="city-modal"
    >
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl">
        {/* Hero image */}
        <div className="relative h-52 shrink-0 overflow-hidden bg-stone-200">
          <img
            src={city.image}
            alt={city.cityName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/30 hover:bg-black/60 border border-white/30 flex items-center justify-center text-white transition-all duration-200"
          >
            <X className="w-4 h-4" />
          </button>

          {/* City name over image */}
          <div className="absolute bottom-4 left-5">
            <div className="flex items-center gap-1.5 text-white/70 text-xs mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{city.tagline}</span>
            </div>
            <h2 className="font-display text-3xl text-white">{city.cityName}</h2>
          </div>
        </div>

        {/* Description */}
        <div className="px-5 pt-4 pb-2 shrink-0 border-b border-stone-100">
          <p className="text-stone-600 text-sm leading-relaxed">{city.description}</p>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 shrink-0 border-b border-stone-100">
          <div className="flex gap-0">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-all duration-200 ${tab === id
                  ? "border-stone-900 text-stone-900"
                  : "border-transparent text-stone-400 hover:text-stone-600"
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content — scrollable */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          {tab === "whatToDo" && (
            <ul className="space-y-2.5">
              {city.whatToDo.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center shrink-0 mt-0.5 text-[0.6rem] font-bold">
                    {i + 1}
                  </div>
                  <span className="text-stone-700 text-sm leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          )}

          {tab === "cafes" && (
            <ul className="space-y-3">
              {city.cafes.map((cafe, i) => {
                const [name, ...rest] = cafe.split(" — ");
                return (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-stone-50 border border-stone-100">
                    <div className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center shrink-0">
                      <Coffee className="w-3.5 h-3.5 text-stone-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-semibold text-stone-900 text-sm">{name}</div>
                      {rest.length > 0 && (
                        <div className="text-stone-500 text-xs mt-0.5">{rest.join(" — ")}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {tab === "itinerary" && (
            <div className="space-y-4">
              {city.itinerary.map((item, i) => (
                <div key={i} className="flex gap-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center text-[0.6rem] font-bold shrink-0">
                      D{i + 1}
                    </div>
                    {i < city.itinerary.length - 1 && (
                      <div className="w-px flex-1 bg-stone-200 mt-2" />
                    )}
                  </div>
                  <div className="pb-4">
                    <div className="font-semibold text-stone-900 text-sm mb-1">{item.day}</div>
                    <p className="text-stone-500 text-sm leading-relaxed">{item.plan}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="shrink-0 px-5 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-3">
          <p className="text-xs text-stone-500">
            We're curating exclusive stays in {city.cityName}. Get notified when they go live.
          </p>
          <a
            href="/contact"
            className="btn-primary text-xs whitespace-nowrap"
          >
            Notify Me
          </a>
        </div>
      </div>
    </div>
  );
}
